import type { JSX } from 'react'
import { useLiveDesk } from './useLiveDesk'
import { ReadOnlyDeskCanvas } from './ReadOnlyDeskCanvas'

// The public page for a live desk. Its job beyond rendering is to be honest
// about what the viewer is looking at: live, reconnecting on an older revision,
// paused by the owner, or revoked. A public page that silently shows stale
// content as though it were current is the failure mode worth designing out.

function StatusChip({
  state,
  stale,
  publishedAt
}: {
  state: string
  stale: boolean
  publishedAt: number | null
}): JSX.Element {
  const map: Record<string, { label: string; tone: string }> = {
    live: { label: 'Live', tone: 'text-emerald-300 border-emerald-400/30 bg-emerald-400/10' },
    reconnecting: { label: 'Reconnecting', tone: 'text-amber-300 border-amber-400/30 bg-amber-400/10' },
    paused: { label: 'Paused by owner', tone: 'text-stone-300 border-white/15 bg-white/5' },
    revoked: { label: 'Link revoked', tone: 'text-red-300 border-red-400/30 bg-red-400/10' },
    pending: { label: 'Not published yet', tone: 'text-stone-300 border-white/15 bg-white/5' },
    loading: { label: 'Loading', tone: 'text-stone-400 border-white/10 bg-white/5' },
    error: { label: 'Unavailable', tone: 'text-red-300 border-red-400/30 bg-red-400/10' }
  }
  const s = map[state] ?? map.loading
  const when =
    publishedAt && (state === 'reconnecting' || state === 'paused')
      ? ` · updated ${new Date(publishedAt).toLocaleTimeString()}`
      : ''
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] px-2 py-1 rounded-full border ${s.tone}`}
      role="status"
      aria-live="polite"
    >
      <span
        className={`w-1.5 h-1.5 rounded-full ${state === 'live' ? 'bg-emerald-400' : 'bg-current opacity-60'}`}
        aria-hidden="true"
      />
      {s.label}
      {stale && state === 'reconnecting' ? ' · showing last known' : when}
    </span>
  )
}

export function LiveDeskPage({ apiBase, token }: { apiBase: string; token: string }): JSX.Element {
  const desk = useLiveDesk(apiBase, token)

  return (
    <div className="min-h-screen h-screen flex flex-col bg-[#0b0b0c] text-stone-200">
      <header className="px-4 py-2 flex items-center justify-between gap-3 border-b border-white/[0.06] shrink-0">
        <div className="min-w-0 flex items-center gap-3">
          <span className="text-accent" aria-hidden="true">✦</span>
          <span className="text-sm truncate">{desk.projection?.title ?? 'Shared desk'}</span>
        </div>
        <StatusChip state={desk.state} stale={desk.stale} publishedAt={desk.publishedAt} />
      </header>

      {desk.state === 'revoked' && (
        <div className="flex-1 flex items-center justify-center p-8 text-center">
          <div className="max-w-md">
            <div className="text-[11px] uppercase tracking-[0.3em] text-amber-300 mb-2">No longer shared</div>
            <p className="text-sm text-stone-300">
              The owner has stopped sharing this desk. Ask them for a new link if you still need access.
            </p>
          </div>
        </div>
      )}

      {desk.state === 'pending' && (
        <div className="flex-1 flex items-center justify-center p-8 text-center">
          <p className="text-sm text-stone-400">This desk hasn't been published yet.</p>
        </div>
      )}

      {desk.state === 'error' && !desk.projection && (
        <div className="flex-1 flex items-center justify-center p-8 text-center">
          <div className="max-w-md">
            <p className="text-sm text-stone-300 mb-2">This desk couldn't be loaded.</p>
            <p className="text-xs text-stone-500 mb-4">{desk.error}</p>
            <button className="text-xs underline text-accent" onClick={desk.refresh}>
              Try again
            </button>
          </div>
        </div>
      )}

      {desk.state === 'loading' && !desk.projection && (
        <div className="flex-1 flex items-center justify-center">
          <span className="text-sm text-stone-500">Loading desk…</span>
        </div>
      )}

      {/* The last good revision stays on screen through a reconnect rather than
          blanking, with the status chip carrying the truth about its age. */}
      {desk.projection && desk.state !== 'revoked' && (
        <ReadOnlyDeskCanvas projection={desk.projection} apiBase={apiBase} token={token} />
      )}

      <footer className="px-4 py-2 border-t border-white/[0.06] flex items-center justify-between text-[10px] text-stone-500 shrink-0">
        <span>Read-only · shared via Plexii</span>
        <a href="https://plexiidesk.com" className="hover:text-stone-300" target="_blank" rel="noreferrer noopener">
          plexiidesk.com
        </a>
      </footer>
    </div>
  )
}
